import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { APP_ROUTES } from '../../../contants/APP_ROUTES';
import { DEFAULT_IDENTIFICATIONS } from '../../../contants/DEFAULT_IDENTIFICATIONS';
import { PARAMS } from '../../../contants/PARAMS';
import { Salary } from '../../../models/salaries/salary';
import { User } from '../../../models/users/User';
import { UserIdentification } from '../../../models/users/userIdentifications';
import { AlertService } from '../../../services/alert.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { ParameterService } from '../../../services/parameter.service';
import { SalaryService } from '../../../services/salary.service';
import { UserService } from '../../../services/user.service';
import { CustomValidators } from '../../../validators/customValidators';

@Component({
	selector: 'app-user-form',
	templateUrl: './user-form.component.html',
	styleUrls: ['./user-form.component.css']
})
export class UserFormComponent implements OnInit {

	form: FormGroup;
	identifications: FormArray;

	user: User;
	salary: Salary;
	isUpdate: boolean = false;
	allowUpdate: boolean = true;//default mode is new
	loading: boolean = false;

	constructor(
		private _userService: UserService,
		private _activatedRoute: ActivatedRoute,
		private _router: Router,
		private _alertService: AlertService,
		private _parameterService: ParameterService,
		private _authService: AuthenticationService,
		private _salaryService: SalaryService
	) { }

	ngOnInit() {
		this._activatedRoute.params.subscribe(
			val => {
				this.init();
			}
		);
	}

	private init(){		
		let id = this._activatedRoute.snapshot.params['id'];
		this.user = new User();
		this.user.id = id;
		
		this.salary = new Salary();
		this.salary.amount = 0;
		this.salary.id = 0;

		this.form = this.createForm(this.user, this.salary);

		if (id == 0) {
			this.isUpdate = false;
			this.allowUpdate = true;
			this._parameterService.getCachedSystemParameterArray<string>(PARAMS.DEFAULT_USER_IDS, 3600).subscribe(
				returnValue => {
					if (returnValue.length == undefined)
						returnValue = DEFAULT_IDENTIFICATIONS;

					returnValue.forEach(x => {
						let userId = new UserIdentification();
						userId.id = 0;
						userId.cardType = x;

						this.addIdentification(userId);
					});
				}
			);
		}
		else {
			this.getUserDetails(id);
			this._authService.checkRights().subscribe(
				result => {
					if(result != undefined){
						this.allowUpdate = result;
					}
				}
			);
		}
	}

	private getUserDetails(id: number){
		this._userService.getUser(id).subscribe(
			user => {
				if (user == undefined) {
					this.user.id = 0;
				}
				else {
					this.user = user;
					this.form = this.createForm(this.user, this.salary);
					if(this.user.identifications !== undefined){
						this.user.identifications.forEach(x => this.addIdentification(x));
					}

					this.isUpdate = true;

					if(this.user.salaryType === 'MONTHLY'){
						this._userService.getMonthlyUserSalary(this.user.id).subscribe(
							result => {
								if(result != null){
									this.salary = result;
									this.form.get('salary').setValue(this.salary.amount);
									this.form.get('effectiveDate').setValue(this.salary.effectiveDate);										
								}
								else{
									this._alertService.showError("A problem occured while retrieving salary data.")
								}
							}
						);
					}
				}
			}
		);
	}

	private createForm(user: User, salary?: Salary): FormGroup {
		this.identifications = new FormArray([]);
		let newForm = new FormGroup({
			firstName: new FormControl(user.firstName, [
				Validators.required,
				Validators.maxLength(50),
			]),
			middleName: new FormControl(user.middleName, [
				Validators.maxLength(50)
			]),
			lastName: new FormControl(user.lastName, [
				Validators.required,
				Validators.maxLength(50)
			]),
			nickname: new FormControl(user.nickname),
			birthDate: new FormControl(user.birthDate),
			email: new FormControl(user.email, [
				Validators.required,
				Validators.maxLength(50),
				Validators.email
			]),
			altEmail: new FormControl(user.alternativeEmail, [
				Validators.email,
				Validators.maxLength(50)
			]),
			employeeRole: new FormControl(user.employeeRole, [
				Validators.required
			]),
			password: new FormControl('', [
				Validators.required,
				Validators.maxLength(20),
				Validators.minLength(8)
			]),
			passwordVerification: new FormControl('', [
				Validators.required,
				Validators.maxLength(20),
				Validators.minLength(8)
			]),
			salaryType: new FormControl(user.salaryType, [
				Validators.required,
				Validators.maxLength(30)
			]),
			salary: new FormControl(salary.amount, [
				Validators.required,
				CustomValidators.validateCurrency
			]),
			effectiveDate: new FormControl(salary.effectiveDate),
			identifications: this.identifications
		});

		if(this.user.id != 0){
			newForm.controls.password.disable();
			newForm.controls.passwordVerification.disable();
		}
		return newForm;
	}

	private createIdentificationForm(identification: UserIdentification){
		return new FormGroup({
			id: new FormControl(identification.id),
			cardType: new FormControl(identification.cardType, [
				Validators.required,
				Validators.maxLength(30)
			]),
			cardNumber: new FormControl(identification.cardNumber, [
				Validators.required,
				Validators.maxLength(30)
			]),
		});
	}

	onFormSubmit(formValue) {
		if (!this.form.valid || formValue.passwordVerification != formValue.password) {
			this._alertService.showError("Form is invalid, all inputs marked with '*' are required.");
			return; //do something here
		}

		//update user item
		this.user.firstName = formValue.firstName;
		this.user.middleName = formValue.middleName;
		this.user.lastName = formValue.lastName;
		this.user.nickname = formValue.nickname;
		this.user.birthDate = formValue.birthDate;
		this.user.email = formValue.email;
		this.user.alternativeEmail = formValue.altEmail;
		this.user.password = formValue.password;
		this.user.employeeRole = formValue.employeeRole;
		this.user.salaryType = formValue.salaryType;

		this.user.identifications = [];
		formValue.identifications.forEach(x => {
			this.user.identifications.push(x);
		});

		this.loading = true;
		if (this.user.id == undefined || this.user.id == 0) {
			//salary will only be created if you register a new user
			this.salary.amount = formValue.salary;
			this.salary.reason = 'INITIAL';
			this.salary.remarks = '';
			this.salary.effectiveDate = formValue.effectiveDate;

			this.createUser();
		}
		else {
			this.updateUser();
		}
	}

	onRemoveIDClick(index) {
		let containers = this.form.get('identifications') as FormArray;
		containers.removeAt(index);
	}

	onAddIDClick() {
		this.addIdentification();
	}

	private addIdentification(source?: UserIdentification){
		if(source === undefined || source === null){
			source = new UserIdentification();
		}

		this.identifications.push(this.createIdentificationForm(source));
	}

	private createUser() {
		this._userService.addUser(this.user).subscribe(
			result => {
				if (result.object != null) {
					this.user = result.object;

					this.salary.userId = this.user.id;
					this._salaryService.addSalary(this.salary).subscribe(
						result => {
							if (result.object != null) {
								this.salary = result.object;
								this._alertService.showSuccess('User registered.');
								
								this.isUpdate = true;
								this._router.navigate([APP_ROUTES.userForm.replace('/:id', ''), this.user.id]);
							}
							else{
								this._alertService.displayErrorResult(result.errorCollection);
							}
						}
					);
				}
				else {
					this._alertService.displayErrorResult(result.errorCollection);
				}
				this.loading = false;
			}
		);
	}

	private updateUser() {
		this._userService.updateUser(this.user).subscribe(
			result => {
				if (result != undefined && result['object'] != undefined) {
					this.user = result.object;
					this._alertService.showSuccess('User data updated.');
					this._router.navigate([APP_ROUTES.userForm.replace('/:id', ''), this.user.id]);
				}
				else {
					this._alertService.displayErrorResult(result.errorCollection);
				}
				this.loading = false;
			}
		);
	}

	isControlInvalid(controlName: string) {
		let control = this.form.controls[controlName];
		return control.invalid && (control.dirty || control.touched);
	}

	changePassword() {
		this._router.navigate([APP_ROUTES.changePassword]);
	}

	deactivate() {
		this._userService.deactivateUser(this.user.id).subscribe(
			result => {
				if(result == undefined){
					this._alertService.showGenericError();
				}
				else if(result.errors.length > 0){
					this._alertService.displayErrorResult(result);
				}
				else{
					this._alertService.showSuccess(`${this.user.fullName}'s account has been deactivated.`)
				}
			}
		)
	}

	activate() {
		this._userService.activateUser(this.user.id).subscribe(
			result => {
				if(result == undefined){
					this._alertService.showGenericError();
				}
				else if(result.errors.length > 0){
					this._alertService.displayErrorResult(result);
				}
				else{
					this._alertService.showSuccess(`${this.user.fullName}'s account has been "activated".`)
				}
			}
		)
	}

	goToChangePassword(){
		this._router.navigate([APP_ROUTES.changePassword]);
	}

	goToChangeSalary(){
		this._router.navigate([APP_ROUTES.salaryForm.replace('/:id', ''), this.user.id]);
	}

	getIdentifications(){
		return (this.form.get('identifications')) as FormArray;
	}
}



// WEBPACK FOOTER //
// ./src/app/components/users/user-form/user-form.component.ts